import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

const PAPER_TOP_ROUTES = ["/cafe", "/contact"];

const Wordmark = () => (
  <Link to="/" className="flex items-center" data-testid="brand-wordmark" aria-label="Root2Shoot home">
    <img
      src="/images/logo-duo.png"
      alt="Root2Shoot — Sustainable Café and Film & TV Catering"
      className="h-11 w-auto sm:h-12"
    />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const lightTop = !scrolled && PAPER_TOP_ROUTES.includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "border-b border-gold/15 bg-noir/85 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8" aria-label="Main navigation">
        <Wordmark />
        <div className="hidden items-center gap-6 lg:flex xl:gap-8">
          {NAV_LINKS.slice(1).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testid}
              className={({ isActive }) =>
                `border-b pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  lightTop
                    ? `hover:text-gold-deep ${isActive ? "border-gold-deep text-gold-deep" : "border-transparent text-ink/70"}`
                    : `hover:text-gold ${isActive ? "border-gold text-gold" : "border-transparent text-cream/80"}`
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/catering#enquiry"
            className={`${lightTop ? "btn-ink" : "btn-gold"} !px-5 !py-2.5`}
            data-testid="nav-cta-enquire"
          >
            Enquire Now
          </Link>
        </div>
        <button
          className={`flex h-11 w-11 items-center justify-center border transition-colors duration-500 lg:hidden ${
            lightTop ? "border-ink/30 text-ink" : "border-cream/25 text-cream"
          }`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 top-[76px] z-40 flex flex-col bg-noir/98 backdrop-blur-2xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-1 flex-col justify-center px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="border-b border-gold/15"
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-${link.testid}`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-5 py-4 transition-colors ${
                        isActive ? "text-gold" : "text-cream hover:text-gold"
                      }`
                    }
                  >
                    <span className="font-mono text-[10px] tracking-[0.3em] text-gold/70">0{i + 1}</span>
                    <span className="font-editorial text-4xl tracking-tight">{link.label}</span>
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-10"
              >
                <Link to="/catering#enquiry" className="btn-gold" data-testid="mobile-nav-cta-enquire">
                  Enquire About Catering
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
